import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { AssetBookingRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { isDefined } from "@/helpers/util";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useSlug } from "@/hooks/useSlug";
import { getTimeFromMinutesOfDay } from "@/modules/bookings/helpers";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";
import { routes } from "@/routes";

import { getMinBookingDate, getMaxBookingDate } from "./booking-dates";
import { BookingForm, type BookingFormProps, type CreateOrEditBookingFormValues } from "./BookingForm";

export function CreateBookingPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  // Keep the initial selection independent of the clock that refreshes booking limits.
  const [entryDate] = useState(() => new Date());
  const [date, setDate] = useState<Date>();

  const slug = useSlug();
  const { aid: assetId } = useParams<{ aid: string }>();
  const navigate = useNavigate();

  const bookableAssetQueries = useBookableAssetsQueries();
  const {
    data: assetDetails,
    isPending: isPendingAssetDetails,
    error: errorAssetDetails,
  } = useQuery(
    bookableAssetQueries.details({
      assetId: assetId!,
    }),
  );
  const query = date ? { Date: format(date, "yyyy-MM-dd") } : undefined;
  const {
    data: assetSlots,
    isPending: isPendingAssetSlots,
    error: errorAssetSlots,
  } = useQuery(bookableAssetQueries.slots({ assetId: assetId!, query }));
  const { data: assetStatistics, error: assetStatisticsError } = useQuery({
    ...bookableAssetQueries.statistics({ assetId: assetId! }),
    enabled: assetDetails?.canDelete === true,
  });

  const deleteAsset = bookableAssetMutations.useDeleteAsset();
  const createBookingMutation = bookableAssetMutations.useCreateBooking(assetId!);

  async function createBooking(data: CreateOrEditBookingFormValues) {
    const req: AssetBookingRequest = {
      date: format(data.date, "yyyy-MM-dd"),
      startTime: getTimeFromMinutesOfDay(data.startTime!),
      endTime: getTimeFromMinutesOfDay(data.endTime!),
      reason: data.reason,
    };

    return await createBookingMutation.mutateAsync(req);
  }

  const minBookingDate = getMinBookingDate(assetDetails, clockDate);
  const maxBookingDate = getMaxBookingDate(assetDetails, clockDate);

  const defaultDate = useMemo(() => {
    if (!assetDetails) {
      // Should never be used (as we require asset details to be loaded before render), but just in case
      return entryDate;
    }

    return getMinBookingDate(assetDetails, entryDate);
  }, [assetDetails, entryDate]);

  const defaultStartTime = useMemo(() => {
    return null;
  }, []);
  const defaultEndTime = useMemo(() => {
    return null;
  }, []);

  useEffect(() => {
    if (assetDetails?.id) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setDate(defaultDate);
    }
  }, [assetDetails?.id, defaultDate]);

  const error = !isDefined(assetId) || errorAssetDetails || assetStatisticsError || errorAssetSlots;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isPendingAssetDetails;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const formProps: BookingFormProps = {
    assetDetails,
    assetSlots: assetSlots?.slots || [],
    isLoadingSlots: isPendingAssetSlots,
    bookingDetails: undefined,
    defaultValues: {
      date: defaultDate,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      reason: undefined,
      isRegulationAccepted: false,
    },
    minDate: minBookingDate,
    maxDate: maxBookingDate,
    onDateChange: setDate,
    onSubmit: createBooking,
    isSubmitting: createBookingMutation.isPending || deleteAsset.isPending,
    futureBookings: assetStatistics?.futureBookingsCount ?? 0,
    onDelete: (payload) =>
      deleteAsset.mutateAsync(payload, {
        onSuccess: () => void navigate(routes.bookings.list({ slug })),
      }),
  };

  return <BookingForm {...formProps} />;
}
